import * as React from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Container,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Toolbar,
  Typography,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tabs,
  Tab,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CodeIcon from "@mui/icons-material/Code";
import Footer from "../components/Footer.jsx";
import JsonDataModal from "../components/admin/JsonDataModal.jsx";
import Navigation from "../components/Navigation.jsx";
import { useAuth0 } from "@auth0/auth0-react";
import { useSnackbar } from "../hooks/useSnackbar.js";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { SUBSCRIPTION_PLANS_VENDORS, SUBSCRIPTION_PLANS_TERMS } from "../constants/admin.js";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8787";

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function AdminSubscriptionPlanDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAccessTokenSilently } = useAuth0();
  const { showSuccess, showError, SnackbarComponent } = useSnackbar();

  const getInitialTab = () => {
    const tabParam = searchParams.get("tab");
    if (tabParam) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 1) return tabIndex;
    }
    return 0;
  };
  const [tabValue, setTabValue] = React.useState(getInitialTab);

  const [plan, setPlan] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  // Edit form state
  const [editName, setEditName] = React.useState("");
  const [editTerm, setEditTerm] = React.useState("");
  const [editActive, setEditActive] = React.useState(true);
  const [editPrice, setEditPrice] = React.useState("");
  const [editVendor, setEditVendor] = React.useState("");
  const [updatingPlan, setUpdatingPlan] = React.useState(false);
  const [updatePlanError, setUpdatePlanError] = React.useState(null);
  const [subscriptions, setSubscriptions] = React.useState([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = React.useState(false);
  const [planJsonModalOpen, setPlanJsonModalOpen] = React.useState(false);
  const [metaDataModalOpen, setMetaDataModalOpen] = React.useState(false);
  const [editMetaData, setEditMetaData] = React.useState("");
  const [updatingMetaData, setUpdatingMetaData] = React.useState(false);
  const [metaDataError, setMetaDataError] = React.useState(null);

  const fetchPlan = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      const response = await fetch(`${API_BASE_URL}/admin/subscription-plans/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch subscription plan: ${response.status}`);
      }

      const data = await response.json();
      setPlan(data);
      setEditName(data.name || "");
      setEditTerm(data.term || "");
      setEditActive(data.active !== undefined ? data.active : true);
      setEditPrice(data.price || "");
      setEditVendor(data.vendor || "");
    } catch (err) {
      console.error("Error fetching subscription plan:", err);
      setError(err.message || "Failed to fetch subscription plan");
    } finally {
      setLoading(false);
    }
  }, [id, getAccessTokenSilently]);

  const fetchSubscriptions = React.useCallback(async () => {
    if (!id) return;
    setLoadingSubscriptions(true);
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      const response = await fetch(`${API_BASE_URL}/admin/subscription-plans/${id}/subscriptions`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch subscriptions: ${response.status}`);
      }

      const data = await response.json();
      setSubscriptions(Array.isArray(data) ? data : data?.data || []);
    } catch (err) {
      console.error("Error fetching plan subscriptions:", err);
      setSubscriptions([]);
    } finally {
      setLoadingSubscriptions(false);
    }
  }, [id, getAccessTokenSilently]);

  React.useEffect(() => {
    fetchPlan();
    fetchSubscriptions();
  }, [fetchPlan, fetchSubscriptions]);

  const handleUpdatePlan = async () => {
    if (!plan) return;

    setUpdatingPlan(true);
    setUpdatePlanError(null);

    try {
      const token = await getAccessTokenSilently().catch(() => null);
      const updateData = {
        name: editName,
        term: editTerm || null,
        active: editActive,
        price: editPrice || null,
        vendor: editVendor || null,
        replaced_by: plan.replaced_by || null,
      };

      const response = await fetch(`${API_BASE_URL}/admin/subscription-plans/${plan.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        let errorMessage = "Failed to update subscription plan";
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          // Use default error message
        }
        throw new Error(errorMessage);
      }

      const updatedPlan = await response.json();
      setPlan(updatedPlan);
      setUpdatePlanError(null);
      showSuccess("Subscription plan updated successfully");
    } catch (err) {
      console.error("Error updating subscription plan:", err);
      const msg = err.message || "Failed to update subscription plan";
      setUpdatePlanError(msg);
      showError(msg);
    } finally {
      setUpdatingPlan(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSearchParams({ tab: newValue.toString() });
  };

  const handleOpenMetaDataModal = () => {
    setEditMetaData(
      plan?.meta_data != null ? JSON.stringify(plan.meta_data, null, 2) : "{}"
    );
    setMetaDataError(null);
    setMetaDataModalOpen(true);
  };

  const handleSaveMetaData = async () => {
    if (!plan) return;
    setMetaDataError(null);
    let parsed;
    try {
      parsed = editMetaData.trim() === "" ? null : JSON.parse(editMetaData);
    } catch {
      setMetaDataError("Invalid JSON");
      return;
    }
    setUpdatingMetaData(true);
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      const response = await fetch(
        `${API_BASE_URL}/admin/subscription-plans/${plan.id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ meta_data: parsed }),
        }
      );
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || errData.error || "Failed to update");
      }
      const updatedPlan = await response.json();
      setPlan(updatedPlan);
      setMetaDataModalOpen(false);
      showSuccess("Meta data updated successfully");
    } catch (err) {
      console.error("Error updating meta_data:", err);
      const msg = err.message || "Failed to update meta_data";
      setMetaDataError(msg);
      showError(msg);
    } finally {
      setUpdatingMetaData(false);
    }
  };

  React.useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 1) {
        setTabValue(tabIndex);
      }
    } else {
      setTabValue(0);
    }
  }, [searchParams]);

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar sx={{ minHeight: "48px !important", height: "48px" }} />
          <Box sx={{ px: 3, pt: 1, pb: 1 }}>
            <Breadcrumbs aria-label="breadcrumb">
              <Typography
                color="primary"
                onClick={() => navigate("/dashboard")}
                sx={{
                  textDecoration: "none",
                  cursor: "pointer",
                  fontWeight: 500,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Admin
              </Typography>
              <Typography
                color="primary"
                onClick={() => navigate("/dashboard/subscription-plans")}
                sx={{
                  textDecoration: "none",
                  cursor: "pointer",
                  fontWeight: 500,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Subscription Plans
              </Typography>
              <Typography color="text.primary" fontWeight={500}>
                {loading ? "Loading..." : plan?.name || "Detail"}
              </Typography>
            </Breadcrumbs>
          </Box>
          <Container maxWidth={false} sx={{ mt: 2, mb: 4, flexGrow: 1, px: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {updatePlanError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {updatePlanError}
              </Alert>
            )}

            {loading ? (
              <Card>
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                    <Skeleton height={40} width={300} />
                    <Skeleton height={32} width={80} borderRadius={4} />
                  </Box>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Skeleton height={56} />
                    </Grid>
                    <Grid item xs={12}>
                      <Skeleton height={56} />
                    </Grid>
                    <Grid item xs={12}>
                      <Skeleton height={56} />
                    </Grid>
                    <Grid item xs={12}>
                      <Skeleton height={56} />
                    </Grid>
                    <Grid item xs={12}>
                      <Skeleton height={56} />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ pt: 2, borderTop: 1, borderColor: "divider" }}>
                        <Skeleton height={40} width={150} />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ) : plan ? (
              <Card>
                <Box sx={{ px: 3, pt: 2, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="h4" component="h1" fontWeight="bold">
                      {plan.name || "Subscription Plan"}
                    </Typography>
                    <Chip
                      label={plan.active ? "Active" : "Inactive"}
                      color={plan.active ? "success" : "default"}
                      size="medium"
                    />
                  </Box>
                  {plan && (
                    <Button
                      variant="outlined"
                      startIcon={<CodeIcon />}
                      onClick={() => setPlanJsonModalOpen(true)}
                      size="small"
                    >
                      View JSON
                    </Button>
                  )}
                </Box>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs value={tabValue} onChange={handleTabChange}>
                    <Tab label="Edit" />
                    <Tab label="Subscriptions" />
                  </Tabs>
                </Box>
                <TabPanel value={tabValue} index={0}>
                  <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 4, alignItems: "flex-start" }}>
                    <Box sx={{ flex: "1 1 50%", minWidth: 0, width: "100%" }}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                          <Box sx={{ flex: "1 1 calc(50% - 12px)", minWidth: 200 }}>
                            <TextField
                              fullWidth
                              label="Plan Name (name)"
                              value={editName}
                              onChange={(e) => {
                                setEditName(e.target.value);
                                setUpdatePlanError(null);
                              }}
                              disabled={updatingPlan}
                              required
                            />
                          </Box>
                          <Box sx={{ flex: "1 1 calc(50% - 12px)", minWidth: 200 }}>
                            <FormControl fullWidth>
                              <InputLabel>Term (term)</InputLabel>
                              <Select
                                value={editTerm}
                                onChange={(e) => {
                                  setEditTerm(e.target.value);
                                  setUpdatePlanError(null);
                                }}
                                label="Term (term)"
                                disabled={updatingPlan}
                              >
                              <MenuItem value="">None</MenuItem>
                              {[...SUBSCRIPTION_PLANS_TERMS]
                                .concat(editTerm && !SUBSCRIPTION_PLANS_TERMS.includes(editTerm) ? [editTerm] : [])
                                .map((t) => (
                                  <MenuItem key={t} value={t}>
                                    {t.charAt(0).toUpperCase() + t.slice(1)}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                        </Box>
                        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                          <Box sx={{ flex: "1 1 calc(50% - 12px)", minWidth: 200 }}>
                            <TextField
                              fullWidth
                                label="Price (price)"
                              type="text"
                              value={editPrice}
                              onChange={(e) => {
                                setEditPrice(e.target.value);
                                setUpdatePlanError(null);
                              }}
                              disabled={updatingPlan}
                              placeholder="0.00"
                              InputProps={{
                                startAdornment: <Typography sx={{ mr: 1, color: "text.secondary" }}>$</Typography>,
                              }}
                            />
                          </Box>
                          <Box sx={{ flex: "1 1 calc(50% - 12px)", minWidth: 200 }}>
                            <FormControl fullWidth>
                              <InputLabel>Status (active)</InputLabel>
                              <Select
                                value={editActive}
                                onChange={(e) => {
                                  setEditActive(e.target.value);
                                  setUpdatePlanError(null);
                                }}
                                label="Status (active)"
                                disabled={updatingPlan}
                              >
                              <MenuItem value={true}>Active</MenuItem>
                              <MenuItem value={false}>Inactive</MenuItem>
                            </Select>
                            </FormControl>
                          </Box>
                        </Box>
                        <Box>
                          <FormControl fullWidth>
                            <InputLabel>Vendor (vendor)</InputLabel>
                            <Select
                              value={editVendor}
                              onChange={(e) => {
                                setEditVendor(e.target.value);
                                setUpdatePlanError(null);
                              }}
                              label="Vendor (vendor)"
                              disabled={updatingPlan}
                            >
                              <MenuItem value="">None</MenuItem>
                              {[...SUBSCRIPTION_PLANS_VENDORS]
                                .concat(editVendor && !SUBSCRIPTION_PLANS_VENDORS.includes(editVendor) ? [editVendor] : [])
                                .map((v) => (
                                  <MenuItem key={v} value={v}>
                                    {v}
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                        </Box>
                        <Box>
                          <TextField
                            fullWidth
                            label="Replaced By (replaced_by)"
                            value={plan.replaced_by_plan_name || "None"}
                            InputProps={{ readOnly: true }}
                            disabled={updatingPlan}
                          />
                        </Box>
                        <Box sx={{ display: "flex", gap: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
                            <Button
                              variant="contained"
                              onClick={handleUpdatePlan}
                              disabled={updatingPlan || !editName.trim()}
                              startIcon={updatingPlan ? <CircularProgress size={16} /> : <EditIcon />}
                            >
                              {updatingPlan ? "Updating..." : "Update Plan"}
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={() => {
                                setEditName(plan.name || "");
                                setEditTerm(plan.term || "");
                                setEditActive(plan.active !== undefined ? plan.active : true);
                                setEditPrice(plan.price || "");
                                setEditVendor(plan.vendor || "");
                                setUpdatePlanError(null);
                              }}
                              disabled={updatingPlan}
                            >
                              Reset
                            </Button>
                          </Box>
                      </Box>
                    </Box>
                    <Box sx={{ flex: "1 1 50%", width: "100%", minWidth: 0 }}>
                      <Box sx={{ p: 3, borderRadius: 1, bgcolor: "grey.50", border: 1, borderColor: "divider" }}>
                        <Typography variant="h6" gutterBottom fontWeight="bold">
                          Additional Information
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                              Plan ID
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.25 }} fontFamily="monospace">
                              {plan.id || "N/A"}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                              Replaced By Plan
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.25 }}>
                              {plan.replaced_by_plan_name || "None"}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                              Created
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.25 }}>
                              {plan.created_at
                                ? new Date(plan.created_at).toLocaleString()
                                : "N/A"}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                              Updated
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.25 }}>
                              {plan.updated_at
                                ? new Date(plan.updated_at).toLocaleString()
                                : "N/A"}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                              Meta Data
                            </Typography>
                            <Box
                              component="pre"
                              sx={{
                                mt: 0.25,
                                p: 1.5,
                                borderRadius: 1,
                                bgcolor: "grey.100",
                                fontSize: "0.75rem",
                                fontFamily: "monospace",
                                overflow: "auto",
                                maxHeight: 200,
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                              }}
                            >
                              {plan.meta_data != null
                                ? JSON.stringify(plan.meta_data, null, 2)
                                : "—"}
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 0.5, flexWrap: "wrap" }}>
                              <Typography
                                component="button"
                                variant="body2"
                                color="primary"
                                onClick={handleOpenMetaDataModal}
                                sx={{
                                  border: "none",
                                  background: "none",
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                  "&:hover": { textDecoration: "none" },
                                }}
                              >
                                Edit meta_data
                              </Typography>
                              {(() => {
                                const raw = plan?.meta_data;
                                const meta =
                                  raw != null && typeof raw === "object" && !Array.isArray(raw)
                                    ? raw
                                    : typeof raw === "string"
                                      ? (() => {
                                          try {
                                            const p = JSON.parse(raw);
                                            return p && typeof p === "object" ? p : null;
                                          } catch {
                                            return null;
                                          }
                                        })()
                                      : null;
                                const ssPricingId = meta?.squarespace_pricing_id;
                                if (!ssPricingId) return null;
                                return (
                                  <>
                                    <Typography variant="body2" color="text.secondary">
                                      |
                                    </Typography>
                                    <Link
                                      component="button"
                                      variant="body2"
                                      onClick={() =>
                                        navigate(`/dashboard/squarespace/prices/${ssPricingId}`)
                                      }
                                      sx={{
                                        border: "none",
                                        background: "none",
                                        cursor: "pointer",
                                        textDecoration: "underline",
                                        "&:hover": { textDecoration: "none" },
                                      }}
                                    >
                                      View SS Pricing
                                    </Link>
                                  </>
                                );
                              })()}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </TabPanel>
                <TabPanel value={tabValue} index={1}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    All subscriptions tied to this plan ({subscriptions.length})
                  </Typography>
                  <FormHelperText sx={{ mb: 2 }}>
                    Joined on subscriptions.plan = subscription_plans.id
                  </FormHelperText>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Organization</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Start Date</TableCell>
                          <TableCell>End Date</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loadingSubscriptions ? (
                          Array.from({ length: 3 }).map((_, idx) => (
                            <TableRow key={`skeleton-${idx}`}>
                              <TableCell><Skeleton width="60%" height={20} /></TableCell>
                              <TableCell><Skeleton width={60} height={24} borderRadius={4} /></TableCell>
                              <TableCell><Skeleton width="50%" height={20} /></TableCell>
                              <TableCell><Skeleton width="50%" height={20} /></TableCell>
                              <TableCell><Skeleton width="40%" height={20} /></TableCell>
                              <TableCell align="right"><Skeleton circle width={32} height={32} /></TableCell>
                            </TableRow>
                          ))
                        ) : subscriptions.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              <Typography color="text.secondary">
                                No subscriptions found for this plan
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          subscriptions.map((sub) => (
                            <TableRow key={sub.id} hover>
                              <TableCell>
                                <Typography variant="body2">
                                  {sub.dba_name || sub.organization_name || "N/A"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={sub.status || "Unknown"}
                                  color={
                                    sub.status === "active"
                                      ? "success"
                                      : sub.status === "cancelled"
                                        ? "error"
                                        : sub.status === "expired"
                                          ? "warning"
                                          : "default"
                                  }
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                {sub.start_date
                                  ? new Date(sub.start_date).toLocaleDateString()
                                  : "N/A"}
                              </TableCell>
                              <TableCell>
                                {sub.end_date
                                  ? new Date(sub.end_date).toLocaleDateString()
                                  : "N/A"}
                              </TableCell>
                              <TableCell>
                                {sub.amount != null
                                  ? `$${parseFloat(sub.amount).toFixed(2)}`
                                  : "N/A"}
                              </TableCell>
                              <TableCell align="right">
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => navigate(`/dashboard/subscriptions/${sub.id}`)}
                                  title="View subscription"
                                >
                                  <VisibilityIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </TabPanel>
              </Card>
            ) : (
              <Alert severity="warning">Subscription plan not found</Alert>
            )}
          </Container>
          <Footer />
        </Box>
      </Box>
      <JsonDataModal
        open={planJsonModalOpen}
        onClose={() => setPlanJsonModalOpen(false)}
        data={plan}
        title={`Subscription Plan JSON - ${plan ? (plan.name || "Unknown") : ""}`}
      />
      <Dialog
        open={metaDataModalOpen}
        onClose={() => !updatingMetaData && setMetaDataModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Meta Data</DialogTitle>
        <DialogContent dividers>
          {metaDataError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {metaDataError}
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            minRows={10}
            maxRows={20}
            value={editMetaData}
            onChange={(e) => setEditMetaData(e.target.value)}
            placeholder='{"key": "value"}'
            sx={{
              "& textarea": { fontFamily: "monospace", fontSize: "0.875rem" },
            }}
            disabled={updatingMetaData}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => !updatingMetaData && setMetaDataModalOpen(false)}
            disabled={updatingMetaData}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveMetaData}
            disabled={updatingMetaData}
            startIcon={
              updatingMetaData ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <EditIcon />
              )
            }
          >
            {updatingMetaData ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {SnackbarComponent}
    </>
  );
}
